import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './ProjectViewer.css';
import PixelStreamingViewer from './PixelStreamingViewer';
import PixelStreamingIframeViewer from './PixelStreamingIframeViewer';
import unrealProjectsService from '../../../services/unrealProjectsService';
import { PS_SOURCE_URL } from '../../../API/api';
import viewerPresenceService from '../../../services/viewerPresenceService';
import { getProjectVirtualTours, getVirtualTourUrlByCode } from '../../../utils/virtualTourMapping';
import OrientationOverlay from './OrientationOverlay';
import { useIsMobile, useIsPortrait } from '../../../hooks/useIsMobile';
import { useBackClose } from '../../../hooks/useBackClose';

const ProjectViewer = ({ project, onBack }) => {
  // Mobile: viewer is landscape-only. Portrait shows a blocking rotate
  // overlay (iOS path); Android additionally gets screen.orientation.lock
  // once fullscreen is active.
  const isMobile = useIsMobile();
  const isPortrait = useIsPortrait();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Sidebar starts collapsed when /view route is opened
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [streamUrl, setStreamUrl] = useState(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(true);
  const [manualUrl, setManualUrl] = useState('');
  const [showManualUrlInput, setShowManualUrlInput] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Key to force iframe refresh
  const [isViewerConnected, setIsViewerConnected] = useState(false); // Track if 3D viewer has connected
  const commandFunctionsRef = useRef(null);
  const viewerContentRef = useRef(null);
  const iframeViewerRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false); // State for fullscreen prompt overlay (currently unused but kept for potential future use)
  const fullscreenButtonRef = useRef(null);
  const [showFullscreenHint, setShowFullscreenHint] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const fullscreenHintTimeoutRef = useRef(null);

  // Load manual URL from localStorage on mount (for local dev)
  useEffect(() => {
    if (PS_SOURCE_URL === 'local') {
      const savedUrl = localStorage.getItem('pixelStreamingManualUrl');
      if (savedUrl) {
        setManualUrl(savedUrl);
        setStreamUrl(savedUrl);
        setIsLoadingUrl(false);
        console.log('🔧 Using saved manual URL from localStorage:', savedUrl);
      } else {
        setShowManualUrlInput(true);
        setIsLoadingUrl(false);
      }
    }
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Handle refresh of Pixel Streaming connection
  const handleRefreshConnection = useCallback(() => {
    console.log('🔄 Refreshing Pixel Streaming connection...');

    // If we have an iframe viewer ref, call its refresh method
    if (iframeViewerRef.current && iframeViewerRef.current.refresh) {
      iframeViewerRef.current.refresh();
    } else {
      // Otherwise, force re-render by incrementing refresh key
      setRefreshKey(prev => prev + 1);
    }
  }, []);

  // Add keyboard shortcut for refresh (Ctrl+Shift+R)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'R') {
        e.preventDefault();
        if (streamUrl) {
          handleRefreshConnection();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [streamUrl, handleRefreshConnection]);

  // Reset viewer connection state when stream URL or project changes
  useEffect(() => {
    setIsViewerConnected(false);
  }, [streamUrl, project?.Id]);

  // Get Unreal stream URL for this project
  useEffect(() => {
    // Skip fetching if using local mode (manual URL entry)
    if (PS_SOURCE_URL === 'local') {
      return;
    }

    const fetchStreamUrl = async () => {
      if (!project || !project.Id) {
        setIsLoadingUrl(false);
        return;
      }

      try {
        setIsLoadingUrl(true);
        const url = await unrealProjectsService.getProjectExternalUrl(project.Id);
        if (url) {
          // Normalize URL: Remove www. subdomain and ensure correct path
          // The player.html may try to load resources from www.123bimlive.com
          // but the actual working URL is 123bimlive.com (without www)
          // Also ensure we're not loading the root page (which has X-Frame-Options)
          let normalizedUrl = url;
          try {
            const urlObj = new URL(url);
            const originalUrl = urlObj.toString();

            // Pin the iframe to the CURRENT origin so the app is origin-agnostic.
            // The UnrealProjects secret stores a canonical prod URL
            // (https://123bimlive.com/<slug>/player.html). We keep its path + query
            // (the slug + params) but swap the host to whatever domain the user came
            // in through: dev123bimlive.com embeds dev (same-origin), 123bimlive.com
            // embeds prod (unchanged). This removes the cross-origin postMessage /
            // parent-access / X-Frame-Options cascade.
            //
            // STRIP www.: pin to the bare apex, never the www. sub-host. player.html's
            // auth writes the cognito_token cookie on BOTH the host (www.123bimlive.com)
            // and the registrable domain (.123bimlive.com); on a www. host those are two
            // distinct cookies, and player.html's getCookie() returns null when more than
            // one match exists → its periodic check fails → it redirects the iframe to
            // /projects. Pinning to the apex (123bimlive.com / dev123bimlive.com) collapses
            // them to a single cookie. The apex is the proven-working prod config.
            const targetHost = window.location.host.replace(/^www\./, '');
            if (urlObj.host !== targetHost) {
              urlObj.protocol = window.location.protocol;
              urlObj.host = targetHost;
              console.log('🔧 Rewrote iframe host to current origin (apex):', originalUrl, '→', urlObj.toString());
            }

            // Ensure we're not loading the root page (which has X-Frame-Options: sameorigin)
            // If URL is root or doesn't have a path, default to /dev/player.html
            if (urlObj.pathname === '/' || urlObj.pathname === '') {
              console.warn('⚠️ URL is root page, redirecting to /dev/player.html to avoid X-Frame-Options error');
              urlObj.pathname = '/dev/player.html';
            }

            normalizedUrl = urlObj.toString();
            if (normalizedUrl !== originalUrl) {
              console.log('🔧 URL normalized:', originalUrl, '→', normalizedUrl);
            }
          } catch (e) {
            console.warn('⚠️ Could not parse URL for normalization:', e);
          }

          // Use the full player URL as-is since /dev alone doesn't work
          // The player.html page contains the Pixel Streaming setup
          setStreamUrl(normalizedUrl);
          console.log('✅ Found Unreal stream URL:', normalizedUrl);
        } else {
          console.log('ℹ️ No Unreal stream URL found for this project');
        }
      } catch (error) {
        console.error('❌ Error fetching stream URL:', error);
      } finally {
        setIsLoadingUrl(false);
      }
    };

    fetchStreamUrl();
  }, [project]);

  // Handle manual URL submission (for local dev)
  const handleManualUrlSubmit = (e) => {
    e.preventDefault();
    if (manualUrl.trim()) {
      try {
        // Validate URL format
        const urlObj = new URL(manualUrl);

        // Warn about common non-Pixel Streaming URLs
        const hostname = urlObj.hostname.toLowerCase();
        if (hostname.includes('youtube.com') || hostname.includes('youtu.be') ||
          hostname.includes('vimeo.com') || hostname.includes('dailymotion.com')) {
          alert('⚠️ This appears to be a video streaming URL, not a Pixel Streaming URL.\n\n' +
            'Pixel Streaming URLs should point to an Unreal Engine Pixel Streaming server.\n' +
            'Example: https://123bimlive.com/dev/player.html?MatchViewportRes=true&HoveringMouse=true\n\n' +
            'If you want to test with this URL anyway, it may not work properly.');
        }

        // Set the URL (we allow it even if it's not a Pixel Streaming URL, in case user wants to test)
        setStreamUrl(manualUrl);
        localStorage.setItem('pixelStreamingManualUrl', manualUrl);
        setShowManualUrlInput(false);
        console.log('✅ Manual URL set:', manualUrl);
        console.log('⚠️ Note: This URL may not be a Pixel Streaming server. If it doesn\'t work, use a valid Pixel Streaming URL.');
      } catch (error) {
        alert('❌ Invalid URL format.\n\n' +
          'Please enter a valid URL starting with http:// or https://\n' +
          'Example: https://123bimlive.com/dev/player.html?MatchViewportRes=true&HoveringMouse=true');
      }
    }
  };

  // Handle manual URL change
  const handleManualUrlChange = (e) => {
    setManualUrl(e.target.value);
  };

  // Clear manual URL
  const handleClearManualUrl = () => {
    setManualUrl('');
    setStreamUrl(null);
    localStorage.removeItem('pixelStreamingManualUrl');
    setShowManualUrlInput(true);
  };

  // Handle command functions from Pixel Streaming viewer
  const handleCommandReady = (commandFunctions) => {
    commandFunctionsRef.current = commandFunctions;
    console.log('✅ Command functions ready');
    // Mark viewer as connected - this will trigger heartbeat tracking
    setIsViewerConnected(true);
    console.log('✅ ProjectViewer: 3D viewer connected and ready - starting heartbeat tracking');
  };

  // "?" opens (or closes) the Help screen inside Unreal, like its H / F1 keys.
  const handleOpenHelp = (event) => {
    event?.currentTarget?.blur();
    const sent = iframeViewerRef.current?.emitUIInteraction?.({ action: 'help' });
    if (!sent) {
      console.warn('⚠️ Help: the 3D viewer is not ready yet');
      return;
    }
    // Give the keyboard back to the stream, so Esc closes the help.
    const iframe = iframeViewerRef.current?.iframeRef?.current;
    try { iframe?.focus(); iframe?.contentWindow?.focus(); } catch (e) { /* cross-origin or not ready */ }
  };

  // Handle fullscreen toggle
  const handleFullscreen = async (event) => {
    if (!viewerContentRef.current) {
      console.warn('⚠️ Cannot enter fullscreen: viewerContentRef.current is null');
      return;
    }

    // Prevent the button from keeping focus (which would block keyboard events)
    if (event && event.target) {
      event.target.blur();
    }

    if (!isFullscreen) {
      // Enter fullscreen
      const enterFullscreen = async () => {
        const element = viewerContentRef.current;
        if (!element) {
          console.error('❌ Cannot enter fullscreen: element is null');
          return;
        }

        console.log('🚀 Attempting to enter fullscreen...');
        console.log('🚀 Element:', element);
        console.log('🚀 Element classes:', element.className);

        try {
          // Try standard fullscreen API first
          if (element.requestFullscreen) {
            console.log('🚀 Using requestFullscreen()');
            await element.requestFullscreen();
            console.log('✅ requestFullscreen() called successfully');
          } else if (element.webkitRequestFullscreen) {
            console.log('🚀 Using webkitRequestFullscreen()');
            await element.webkitRequestFullscreen();
            console.log('✅ webkitRequestFullscreen() called successfully');
          } else if (element.msRequestFullscreen) {
            console.log('🚀 Using msRequestFullscreen()');
            await element.msRequestFullscreen();
            console.log('✅ msRequestFullscreen() called successfully');
          } else {
            console.error('❌ No fullscreen API available');
            // Fallback: at least apply CSS classes
            setIsFullscreen(true);
          }
        } catch (err) {
          console.error('❌ Error entering fullscreen:', err);
          console.error('❌ Error details:', {
            name: err.name,
            message: err.message,
            stack: err.stack
          });
          // Fallback: at least apply CSS classes for visual fullscreen
          setIsFullscreen(true);
        }
      };

      await enterFullscreen();

      // Mobile: try to lock to landscape (Android Chrome — requires
      // fullscreen first; iOS throws and the portrait overlay handles it).
      if (isMobile && window.screen?.orientation?.lock) {
        try {
          await window.screen.orientation.lock('landscape');
          console.log('✅ Orientation locked to landscape');
        } catch (e) {
          console.debug('Orientation lock unavailable:', e?.message);
        }
      }

      // After entering fullscreen, ensure iframe gets focus
      setTimeout(() => {
        const iframe = iframeViewerRef.current?.iframeRef?.current;
        if (iframe) {
          try {
            iframe.focus();
            console.log('✅ Focused iframe after fullscreen activation');
          } catch (e) {
            console.debug('Could not focus iframe:', e);
          }
        }
      }, 300);
    } else {
      // Exit fullscreen
      console.log('🚪 Exiting fullscreen...');
      try { window.screen?.orientation?.unlock?.(); } catch (e) { /* no-op */ }
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
        console.log('✅ Exited fullscreen');
      } catch (err) {
        console.error('❌ Error exiting fullscreen:', err);
        setIsFullscreen(false);
      }
    }
  };

  // Handle fullscreen change events and keyboard event forwarding
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );

      console.log('🔄 Fullscreen change detected:', {
        isCurrentlyFullscreen,
        fullscreenElement: document.fullscreenElement,
        webkitFullscreenElement: document.webkitFullscreenElement
      });

      setIsFullscreen(isCurrentlyFullscreen);

      // Hide fullscreen hint when entering fullscreen
      if (isCurrentlyFullscreen) {
        setShowFullscreenHint(false);
      }

      // Update body class to help CSS hide sidebar
      if (isCurrentlyFullscreen) {
        document.body.classList.add('project-viewer-fullscreen-active');
      } else {
        document.body.classList.remove('project-viewer-fullscreen-active');
      }

      // When entering fullscreen, refocus the iframe to restore keyboard input
      if (isCurrentlyFullscreen && iframeViewerRef.current) {
        // Small delay to ensure fullscreen transition completes
        setTimeout(() => {
          const iframe = iframeViewerRef.current?.iframeRef?.current;
          if (iframe) {
            try {
              // Focus the iframe to restore keyboard input
              iframe.focus();
              // Also try focusing via contentWindow if available (same-origin only)
              if (iframe.contentWindow) {
                try {
                  iframe.contentWindow.focus();
                } catch (e) {
                  // Cross-origin - expected to fail
                }
              }
              console.log('✅ Refocused iframe after entering fullscreen');
            } catch (e) {
              console.debug('Could not focus iframe:', e);
            }
          }
        }, 100);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Ensure iframe receives focus and keyboard events when in fullscreen mode
  useEffect(() => {
    if (!isFullscreen || !viewerContentRef.current) return;

    const focusIframe = () => {
      const iframe = iframeViewerRef.current?.iframeRef?.current;
      if (!iframe) {
        return;
      }

      try {
        // Method 1: Direct focus on iframe
        iframe.focus();

        // Method 2: Try focusing via contentWindow (same-origin only)
        if (iframe.contentWindow) {
          try {
            iframe.contentWindow.focus();
          } catch (e) {
            // Cross-origin - expected to fail, that's okay
          }
        }

        console.log('✅ Focused iframe for keyboard input');
        return true;
      } catch (e) {
        console.debug('Could not focus iframe:', e);
        return false;
      }
    };

    // Focus when fullscreen is activated
    focusIframe();

    // Also focus after delays to ensure fullscreen transition is complete
    const timeoutId1 = setTimeout(focusIframe, 100);
    const timeoutId2 = setTimeout(focusIframe, 300);

    // Also focus when the container receives focus (but immediately delegate to iframe)
    const handleContainerFocus = (e) => {
      // If the container gets focus, immediately delegate to iframe
      if (e.target === viewerContentRef.current) {
        // Prevent the container from keeping focus
        setTimeout(() => {
          focusIframe();
        }, 0);
      }
    };

    // Focus when container is clicked (common interaction in fullscreen)
    const handleContainerClick = (e) => {
      // Only focus iframe if clicking directly on the container (not on iframe)
      if (e.target === viewerContentRef.current || e.target.classList.contains('viewer-main')) {
        setTimeout(() => {
          focusIframe();
        }, 0);
      }
    };

    const container = viewerContentRef.current;
    container.addEventListener('focus', handleContainerFocus, true);
    container.addEventListener('click', handleContainerClick, true);

    // Make container focusable but non-interactive for keyboard events
    // This allows it to receive initial focus, then we delegate to iframe
    container.setAttribute('tabindex', '-1');
    container.style.outline = 'none'; // Remove focus outline

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      container.removeEventListener('focus', handleContainerFocus, true);
      container.removeEventListener('click', handleContainerClick, true);
    };
  }, [isFullscreen]);

  // Auto-fullscreen logic removed - users can manually click the fullscreen button if desired

  // Show subtle hint tooltip for fullscreen button on every /view page load
  useEffect(() => {
    // Only show hint when stream URL is loaded (viewer is ready)
    if (!streamUrl || isLoadingUrl || isFullscreen) {
      return;
    }

    // Show hint after a short delay (2 seconds) to let the page settle
    fullscreenHintTimeoutRef.current = setTimeout(() => {
      console.log('🎯 Showing fullscreen hint tooltip');
      setShowFullscreenHint(true);
      
      // Auto-dismiss after 5 seconds with fade-out
      setTimeout(() => {
        console.log('🎯 Fading out fullscreen hint tooltip (auto-dismiss)');
        setIsFadingOut(true);
        // Remove from DOM after fade-out animation completes (300ms)
        setTimeout(() => {
          setShowFullscreenHint(false);
          setIsFadingOut(false);
        }, 300);
      }, 5000);
    }, 2000);

    return () => {
      if (fullscreenHintTimeoutRef.current) {
        clearTimeout(fullscreenHintTimeoutRef.current);
      }
    };
  }, [streamUrl, isLoadingUrl, isFullscreen]);

  // Hide hint when user clicks fullscreen button or dismisses it
  const handleDismissFullscreenHint = () => {
    setIsFadingOut(true);
    // Remove from DOM after fade-out animation completes (300ms)
    setTimeout(() => {
      setShowFullscreenHint(false);
      setIsFadingOut(false);
    }, 300);
    // Don't save to localStorage - show tooltip on every /view page load
  };

  // Viewer presence tracking: Send heartbeat every 30 seconds
  // BUT only after the 3D viewer has actually connected (not just when page loads)
  useEffect(() => {
    if (!project || !project.Id) {
      console.log('💓 ProjectViewer: No project or project.Id, skipping heartbeat setup');
      return;
    }

    if (!isViewerConnected) {
      console.log('💓 ProjectViewer: Waiting for 3D viewer to connect before starting heartbeats');
      return;
    }

    const projectId = project.Id;
    console.log(`💓 ProjectViewer: 3D viewer connected, starting heartbeat for project ${projectId}`);

    // Send initial heartbeat immediately after connection
    viewerPresenceService.sendHeartbeat(projectId)
      .then(result => {
        console.log(`💓 ProjectViewer: Initial heartbeat sent for project ${projectId}:`, result);
      })
      .catch(error => {
        console.warn(`❌ ProjectViewer: Failed to send initial heartbeat for project ${projectId}:`, error);
      });

    // Set up heartbeat interval (every 20 seconds for more frequent updates)
    // This ensures users stay visible even with network delays
    heartbeatIntervalRef.current = setInterval(() => {
      console.log(`💓 ProjectViewer: Sending heartbeat for project ${projectId}...`);
      viewerPresenceService.sendHeartbeat(projectId)
        .then(result => {
          console.log(`💓 ProjectViewer: Heartbeat sent for project ${projectId}:`, result);
        })
        .catch(error => {
          console.warn(`❌ ProjectViewer: Failed to send heartbeat for project ${projectId}:`, error);
        });
    }, 20000); // 20 seconds (timeout is 60s, so 3 missed heartbeats = disconnected)

    // Cleanup: Remove user from active viewers when component unmounts or viewer disconnects
    return () => {
      console.log(`💓 ProjectViewer: Cleaning up heartbeat for project ${projectId}`);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      // Notify server that user left
      viewerPresenceService.leaveProjectView(projectId)
        .then(result => {
          console.log(`💓 ProjectViewer: Left notification sent for project ${projectId}:`, result);
        })
        .catch(error => {
          console.warn(`❌ ProjectViewer: Failed to notify server of leaving project ${projectId}:`, error);
        });
    };
  }, [project, isViewerConnected]); // Add isViewerConnected as dependency

  // Also handle page unload (browser close, navigation away)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (project && project.Id) {
        // Use sendBeacon for reliable delivery on page unload
        const userEmail = sessionStorage.getItem('userEmail');
        if (userEmail) {
          try {
            const data = JSON.stringify({ email: userEmail });
            const url = `${window.location.origin}/api/projects/${project.Id}/viewers/leave`;
            // Use Blob with proper Content-Type for sendBeacon
            const blob = new Blob([data], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
          } catch (error) {
            console.warn('Failed to send leave notification via beacon:', error);
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [project]);


  if (!project) {
    return (
      <div className="project-viewer-container">
        <div className="viewer-error">
          <h2>Project not found</h2>
          <button onClick={onBack}>Back to Project</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`project-viewer-container ${isFullscreen ? 'fullscreen-container' : ''}`}>
      {/* Viewer Header */}
      <div className={`viewer-header ${isFullscreen ? 'hidden' : ''}`}>
        <button
          className="viewer-back-btn"
          onClick={() => {
            // Navigate to projects page
            window.location.href = 'https://www.123bimlive.com/projects';
          }}
        >
          <i className="fas fa-arrow-left"></i>
          Back
        </button>

        <div className="viewer-title">
          <h1>{project.Name} - 3D Viewer</h1>
        </div>

        <div className="viewer-header-right">
          <div className="viewer-project-actions">
            <button 
              className="action-link files"
              onClick={() => navigate(`/projects/${project.Id}`)}
              title="Project Files"
            >
              <i className="fas fa-folder-open"></i>
            </button>
            
            <VirtualTourButton 
              project={project} 
              isExpired={false}
              iconOnly={true}
            />
          </div>
          <div className="viewer-controls">
          {PS_SOURCE_URL === 'local' && (
            <div style={{
              padding: '0.4rem 0.8rem',
              borderRadius: '6px',
              background: 'rgba(59, 130, 246, 0.2)',
              border: '1px solid rgba(255, 72, 0, 0.82)',
              // color: '#93c5fd',
              color: 'red',
              fontSize: '0.75rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}>
              <i className="fas fa-code"></i>
              LOCAL MODE
            </div>
          )}
          {PS_SOURCE_URL === 'local' && streamUrl && (
            <button
              style={{ color: 'red' }}
              className="viewer-btn"
              onClick={() => {
                setShowManualUrlInput(true);
                setStreamUrl(null);
              }}
              title="Change URL"
            >
              <i className="fas fa-edit"></i>
            </button>
          )}
          {streamUrl && (
            <button
              className="viewer-btn"
              onClick={handleOpenHelp}
              title="Help (H / F1 in the 3D view)"
              aria-label="Help"
            >
              <i className="fas fa-question-circle"></i>
            </button>
          )}
          <div className="viewer-btn-fullscreen-wrapper">
            <button
              ref={fullscreenButtonRef}
              className="viewer-btn"
              onClick={(e) => {
                handleFullscreen(e);
                handleDismissFullscreenHint();
              }}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              <i className={isFullscreen ? "fas fa-compress" : "fas fa-expand"}></i>
            </button>
            {showFullscreenHint && (
              <div className={`fullscreen-hint-tooltip ${isFadingOut ? 'fade-out' : ''}`}>
                <div className="fullscreen-hint-content">
                  <i className="fas fa-expand fullscreen-hint-icon"></i>
                  <span>Click here to go Fullscreen</span>
                </div>
                <div className="fullscreen-hint-arrow"></div>
              </div>
            )}
          </div>
          {streamUrl && !isMobile && (
            <button
              className="viewer-btn"
              onClick={handleRefreshConnection}
              title="Refresh Pixel Streaming connection (Ctrl+Shift+R)"
            >
              <i className="fas fa-sync-alt"></i>
            </button>
          )}
          </div>
        </div>
      </div>

      {/* Mobile landscape-only: blocking rotate prompt while portrait */}
      <OrientationOverlay show={isMobile && isPortrait} />

      {/* 3D Viewer Content */}
      <div
        className={`viewer-content ${isFullscreen ? 'fullscreen-mode' : ''}`}
        ref={viewerContentRef}
      >
        {/* Fullscreen Prompt Overlay */}
        {showFullscreenPrompt && (
          <div className="fullscreen-prompt-overlay">
            <div className="fullscreen-prompt-content">
              <div className="fullscreen-prompt-icon">
                <i className="fas fa-expand"></i>
              </div>
              <h3>Your 3D Session is Ready</h3>
              <p>Click below to enter fullscreen mode</p>
              <button
                className="fullscreen-prompt-btn"
                onClick={async () => {
                  console.log('🎯 User clicked "Enter Fullscreen" overlay button');

                  // 1. Call requestFullscreen IMMEDIATELY and DIRECTLY on the element
                  // This must happen before any state updates or async waits to preserve the user gesture
                  const element = viewerContentRef.current;
                  if (element) {
                    try {
                      if (element.requestFullscreen) {
                        await element.requestFullscreen();
                      } else if (element.webkitRequestFullscreen) {
                        await element.webkitRequestFullscreen();
                      } else if (element.msRequestFullscreen) {
                        await element.msRequestFullscreen();
                      }
                      console.log('✅ Native fullscreen request successful');
                    } catch (error) {
                      console.error('❌ Native fullscreen request failed:', error);
                      // Continue anyway to at least set visual fullscreen
                    }
                  }

                  // 2. Update state AFTER the request
                  setShowFullscreenPrompt(false);
                  setIsFullscreen(true); // Ensure visual state is correct

                  // 3. Remove the query parameter from URL
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.delete('fullscreen');
                  setSearchParams(newSearchParams, { replace: true });
                }}
              >
                Enter Fullscreen
              </button>
            </div>
          </div>
        )}

        <div className="viewer-main">
          <div className="viewer-canvas">
            {/* Unreal Engine Pixel Streaming Viewer */}
            {isLoadingUrl ? (
              <div className="viewer-placeholder">
                <div className="placeholder-content">
                  <div className="status-spinner"></div>
                  <h3>Loading Stream...</h3>
                  <p>Connecting to Unreal Engine server...</p>
                </div>
              </div>
            ) : streamUrl ? (
              // Use iframe viewer if URL contains player.html (base path doesn't work)
              streamUrl.includes('/player.html') ? (
                <PixelStreamingIframeViewer
                  key={refreshKey}
                  ref={iframeViewerRef}
                  playerUrl={streamUrl}
                  onCommandReady={handleCommandReady}
                />
              ) : (
                <PixelStreamingViewer
                  key={refreshKey}
                  streamUrl={streamUrl}
                  onCommandReady={handleCommandReady}
                />
              )
            ) : (
              <div className="viewer-placeholder">
                <div className="placeholder-content">
                  {PS_SOURCE_URL === 'local' && showManualUrlInput ? (
                    <>
                      <i className="fas fa-link"></i>
                      <h3>Local Development Mode</h3>
                      <p>Enter Pixel Streaming URL manually</p>
                      <p style={{ fontSize: '0.85rem', opacity: 0.8, marginTop: '0.5rem' }}>
                        ⚠️ Only Unreal Engine Pixel Streaming URLs are supported (not YouTube, Vimeo, etc.)
                      </p>
                      <form onSubmit={handleManualUrlSubmit} style={{ marginTop: '1.5rem', width: '100%', maxWidth: '500px' }}>
                        <input
                          type="text"
                          value={manualUrl}
                          onChange={handleManualUrlChange}
                          placeholder="https://123bimlive.com/dev/player.html?MatchViewportRes=true&HoveringMouse=true"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                            fontSize: '0.9rem',
                            marginBottom: '1rem'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            type="submit"
                            style={{
                              padding: '0.75rem 1.5rem',
                              borderRadius: '8px',
                              border: 'none',
                              background: '#3b82f6',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: '500'
                            }}
                          >
                            Connect
                          </button>
                          {localStorage.getItem('pixelStreamingManualUrl') && (
                            <button
                              type="button"
                              onClick={handleClearManualUrl}
                              style={{
                                padding: '0.75rem 1.5rem',
                                borderRadius: '8px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                background: 'transparent',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                              }}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </form>
                      <p style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '1rem' }}>
                        URL will be saved in localStorage for this session
                      </p>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-cube"></i>
                      <h3>3D Viewer</h3>
                      <p>Project: {project.Name}</p>
                      <p>No Unreal Engine stream available for this project.</p>
                      {PS_SOURCE_URL === 'local' && streamUrl && (
                        <div style={{ marginTop: '1rem' }}>
                          <button
                            onClick={() => {
                              setShowManualUrlInput(true);
                              setStreamUrl(null);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              background: 'transparent',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            Change URL
                          </button>
                        </div>
                      )}
                      <div className="viewer-info">
                        <span>WebGL-based 3D visualization</span>
                        <span>Interactive model exploration</span>
                        <span>Real-time rendering</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Virtual Tour Dropdown Component
const VirtualTourButton = ({ project, isExpired, iconOnly = false }) => {
  const [virtualTours, setVirtualTours] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const [dropdownStyles, setDropdownStyles] = useState({});
  // Mobile: back gesture closes the sheet; header X + backdrop close it too.
  const isMobileSheet = useIsMobile();
  useBackClose(isMobileSheet && showDropdown, () => setShowDropdown(false));

  // Load virtual tours on component mount with retry logic
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second
    
    const loadVirtualTours = async (attempt = 0) => {
      if (!isMounted) return;
      
      setIsLoading(true);
      try {
        const tours = await getProjectVirtualTours(project.Id);
        
        if (!isMounted) return;
        
        // Ensure we got valid data
        if (Array.isArray(tours)) {
          setVirtualTours(tours);
        } else {
          console.warn(`⚠️ VirtualTourButton: Invalid tours data for project ${project.Id}:`, tours);
          setVirtualTours([]);
        }
      } catch (error) {
        console.error(`❌ VirtualTourButton: Failed to load tours for project ${project.Id} (attempt ${attempt + 1}):`, error);
        
        // Retry if we haven't exceeded max retries
        if (attempt < MAX_RETRIES && isMounted) {
          retryCount++;
          console.log(`🔄 VirtualTourButton: Retrying in ${RETRY_DELAY}ms... (${retryCount}/${MAX_RETRIES})`);
          setTimeout(() => {
            if (isMounted) {
              loadVirtualTours(attempt + 1);
            }
          }, RETRY_DELAY);
          return; // Don't set loading to false yet
        }
        
        // If all retries failed, set empty array
        if (isMounted) {
          setVirtualTours([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadVirtualTours();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [project.Id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideToggle = dropdownRef.current?.contains(event.target);
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      if (!clickedInsideToggle && !clickedInsideMenu) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // The menu sizes to its longest tour name, so its width is only known after
  // render: shift it left (before paint) if it would run past the viewport edge.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!showDropdown || isMobileSheet || !menu || dropdownStyles.left == null) return;
    const viewportPadding = 8;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const overflow = menu.getBoundingClientRect().right - (viewportWidth - viewportPadding);
    if (overflow > 0.5) {
      setDropdownStyles(prev => ({
        ...prev,
        left: Math.max(window.scrollX + viewportPadding, prev.left - overflow)
      }));
    }
  }, [showDropdown, isMobileSheet, dropdownStyles]);

  const handleVirtualTourClick = (e, tour) => {
    e.stopPropagation();
    const virtualTourUrl = getVirtualTourUrlByCode(tour.code);
    console.log('🔗 Opening virtual tour URL:', virtualTourUrl, 'for tour:', tour.vtour_name);
    window.open(virtualTourUrl, '_blank', 'noopener,noreferrer');
    setShowDropdown(false);
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!isExpired && virtualTours.length > 0) {
      // Check available space and determine if dropdown should open upwards
      if (!showDropdown && toggleRef.current) {
        const buttonRect = toggleRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // Estimate dropdown height (each item ~56px + padding)
        const estimatedDropdownHeight = Math.min(virtualTours.length * 56 + 32, 400);
        
        const shouldOpenUpwards = spaceBelow < estimatedDropdownHeight && spaceAbove > estimatedDropdownHeight;

        const topPosition = shouldOpenUpwards
          ? buttonRect.top + window.scrollY - estimatedDropdownHeight - 8
          : buttonRect.bottom + window.scrollY + 8;

        // As wide as the longest tour name (max-content), never narrower than
        // the toggle / 280px, never wider than the viewport. The final width is
        // only known after render, so horizontal clamping happens in the
        // layout effect above.
        const viewportPadding = 8;
        const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
        const maxDropdownWidth = viewportWidth - viewportPadding * 2;

        setDropdownStyles({
          position: 'absolute',
          top: topPosition,
          left: Math.max(buttonRect.left + window.scrollX, window.scrollX + viewportPadding),
          width: 'max-content',
          minWidth: Math.min(Math.max(buttonRect.width, 280), maxDropdownWidth),
          maxWidth: maxDropdownWidth,
          maxHeight: estimatedDropdownHeight
        });
      }
      setShowDropdown(!showDropdown);
    } else if (!isExpired && virtualTours.length === 1) {
      // If only one tour, open it directly
      handleVirtualTourClick(e, virtualTours[0]);
    }
  };

  // Don't render if no tours available (and not loading)
  if (!isLoading && virtualTours.length === 0) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <button 
        className="action-link vtour"
        disabled={true}
        title="Loading virtual tours..."
      >
        <i className="fas fa-spinner fa-spin"></i>
        {!iconOnly && "Loading..."}
      </button>
    );
  }

  // If only one tour, render as a simple button
  if (virtualTours.length === 1) {
    return (
      <button 
        className="action-link vtour"
        onClick={(e) => handleVirtualTourClick(e, virtualTours[0])}
        disabled={isExpired || isLoading}
        title={virtualTours[0].vtour_name}
      >
        <i className="fas fa-vr-cardboard"></i>
        {!iconOnly && (isLoading ? 'Loading...' : "Virtual Tour")}
      </button>
    );
  }

  // Render dropdown for multiple tours
  const renderDropdownMenu = () => (
    <>
      {isMobileSheet && (
        <div
          className="vtour-sheet-backdrop"
          onClick={(e) => { e.stopPropagation(); setShowDropdown(false); }}
          aria-hidden="true"
        />
      )}
      <div
        className="vtour-dropdown-menu"
        style={dropdownStyles}
        ref={menuRef}
      >
        {isMobileSheet && (
          <div className="vtour-sheet-header">
            <span>Virtual Tours</span>
            <button
              type="button"
              className="vtour-sheet-close"
              aria-label="Close"
              onClick={(e) => { e.stopPropagation(); setShowDropdown(false); }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
        {virtualTours.map((tour, index) => (
          <div
            key={index}
            className="vtour-dropdown-item"
            onClick={(e) => handleVirtualTourClick(e, tour)}
          >
            <i className="fas fa-vr-cardboard"></i>
            <span className="vtour-name">{tour.vtour_name}</span>
            <span className="vtour-code">({tour.code})</span>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="vtour-dropdown-container" ref={dropdownRef}>
      <button 
        className={`action-link vtour vtour-dropdown-toggle ${showDropdown ? 'active' : ''}`}
        onClick={toggleDropdown}
        disabled={isExpired}
        title="Virtual Tours"
        ref={toggleRef}
      >
        <i className="fas fa-vr-cardboard"></i>
        {!iconOnly && `Virtual Tours`}
        {!iconOnly && <i className={`fas fa-chevron-down dropdown-arrow ${showDropdown ? 'rotate' : ''}`}></i>}
      </button>
      
      {showDropdown && !isExpired && (
        typeof document === 'undefined'
          ? renderDropdownMenu()
          : createPortal(renderDropdownMenu(), document.body)
      )}
    </div>
  );
};

export default ProjectViewer;
